import type { PlayerId } from "../core/ids.ts";

export const INPUT_INTENT_SCHEMA_VERSION = 4 as const;

/**
 * Serializable input sampled for exactly one simulation tick.
 *
 * `moveZ` is positive forward. `lookYaw` is an absolute angle around +Y, with
 * zero looking toward +Z, matching Three.js object yaw.
 */
export interface InputIntent {
  schemaVersion: typeof INPUT_INTENT_SCHEMA_VERSION;
  playerId: PlayerId;
  tick: number;
  moveX: number;
  moveZ: number;
  lookYaw: number;
  jumpPressed: boolean;
  ascendHeld: boolean;
  descendHeld: boolean;
  sprintHeld: boolean;
  interactPressed: boolean;
  cycleSeatPressed: boolean;
  toggleFlightPressed: boolean;
  brakeHeld: boolean;
  reverseHeld: boolean;
  handbrakeHeld: boolean;
  hornHeld: boolean;
  lookBehindHeld: boolean;
  toggleHeadlightsPressed: boolean;
  toggleBeaconPressed: boolean;
  toggleSirenPressed: boolean;
  toggleLeftIndicatorPressed: boolean;
  toggleRightIndicatorPressed: boolean;
  recoverVehiclePressed: boolean;
}

export interface InputIntentValues {
  moveX: number;
  moveZ: number;
  lookYaw: number;
  jumpPressed: boolean;
  ascendHeld: boolean;
  descendHeld: boolean;
  sprintHeld: boolean;
  interactPressed: boolean;
  cycleSeatPressed?: boolean;
  toggleFlightPressed: boolean;
  brakeHeld?: boolean;
  reverseHeld?: boolean;
  handbrakeHeld?: boolean;
  hornHeld?: boolean;
  lookBehindHeld?: boolean;
  toggleHeadlightsPressed?: boolean;
  toggleBeaconPressed?: boolean;
  toggleSirenPressed?: boolean;
  toggleLeftIndicatorPressed?: boolean;
  toggleRightIndicatorPressed?: boolean;
  recoverVehiclePressed?: boolean;
}

export function createInputIntent(
  playerId: PlayerId,
  tick: number,
  values: Readonly<InputIntentValues>,
): InputIntent {
  if (!Number.isSafeInteger(tick) || tick < 0) {
    throw new RangeError("InputIntent.tick must be a non-negative safe integer");
  }

  return {
    schemaVersion: INPUT_INTENT_SCHEMA_VERSION,
    playerId,
    tick,
    moveX: values.moveX,
    moveZ: values.moveZ,
    lookYaw: values.lookYaw,
    jumpPressed: values.jumpPressed,
    ascendHeld: values.ascendHeld,
    descendHeld: values.descendHeld,
    sprintHeld: values.sprintHeld,
    interactPressed: values.interactPressed,
    cycleSeatPressed: values.cycleSeatPressed ?? false,
    toggleFlightPressed: values.toggleFlightPressed,
    brakeHeld: values.brakeHeld ?? false,
    reverseHeld: values.reverseHeld ?? false,
    handbrakeHeld: values.handbrakeHeld ?? false,
    hornHeld: values.hornHeld ?? false,
    lookBehindHeld: values.lookBehindHeld ?? false,
    toggleHeadlightsPressed: values.toggleHeadlightsPressed ?? false,
    toggleBeaconPressed: values.toggleBeaconPressed ?? false,
    toggleSirenPressed: values.toggleSirenPressed ?? false,
    toggleLeftIndicatorPressed: values.toggleLeftIndicatorPressed ?? false,
    toggleRightIndicatorPressed: values.toggleRightIndicatorPressed ?? false,
    recoverVehiclePressed: values.recoverVehiclePressed ?? false,
  };
}

export function createNeutralInputIntent(
  playerId: PlayerId,
  tick: number,
  lookYaw = 0,
): InputIntent {
  return createInputIntent(playerId, tick, {
    moveX: 0,
    moveZ: 0,
    lookYaw,
    jumpPressed: false,
    ascendHeld: false,
    descendHeld: false,
    sprintHeld: false,
    interactPressed: false,
    cycleSeatPressed: false,
    toggleFlightPressed: false,
    brakeHeld: false,
    reverseHeld: false,
    handbrakeHeld: false,
    hornHeld: false,
    lookBehindHeld: false,
    toggleHeadlightsPressed: false,
    toggleBeaconPressed: false,
    toggleSirenPressed: false,
    toggleLeftIndicatorPressed: false,
    toggleRightIndicatorPressed: false,
    recoverVehiclePressed: false,
  });
}
